import React, { useState } from 'react';
import { ErrorOutline as ErrIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';
import {
  ADMIN_CONSOLE_URL, BRAND, CRIMSON, ROOT_DOMAIN, Lnk, StoreBadges, communityUrl, usePageMeta,
} from './shared';

type Found = { id: string; name: string; logoUrl?: string; color?: string; url: string };

// Hosts that don't follow the <orgId>.konklave.app pattern.
const HOST_TO_ORG: Record<string, string> = {
  'members.konklave.app': 'kappa',
  'members.crimsonconnect.app': 'dst',
};

/**
 * Turn whatever the person typed ("myorg", "myorg.konklave.app",
 * "https://myorg.konklave.app/home") into an organization id, or null.
 */
function parseAddress(raw: string): string | null {
  let s = raw.trim().toLowerCase();
  if (!s) return null;
  s = s.replace(/^[a-z]+:\/\//, '').split(/[/?#]/)[0].replace(/^www\./, '');
  if (HOST_TO_ORG[s]) return HOST_TO_ORG[s];
  const suffix = `.${ROOT_DOMAIN}`;
  if (s.endsWith(suffix)) s = s.slice(0, -suffix.length);
  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(s)) return null;
  return s;
}

const SignIn: React.FC = () => {
  usePageMeta('Sign in');
  const [address, setAddress] = useState('');
  const [status, setStatus] = useState<'idle' | 'checking' | 'found' | 'err'>('idle');
  const [error, setError] = useState('');
  const [found, setFound] = useState<Found | null>(null);

  const lookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setFound(null);
    const orgId = parseAddress(address);
    if (!orgId) {
      setStatus('err');
      setError(`Enter your community’s address, for example yourorg.${ROOT_DOMAIN}.`);
      return;
    }
    setStatus('checking');
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/organizations/${encodeURIComponent(orgId)}`);
      if (res.status === 404) {
        setStatus('err');
        setError(`There’s no community at ${orgId}.${ROOT_DOMAIN}. Check the spelling, or ask an officer in your organization for the address.`);
        return;
      }
      if (!res.ok) throw new Error('lookup failed');
      const data = await res.json();
      const logo: string | undefined = data?.branding?.logoUrl;
      setFound({
        id: orgId,
        name: data?.appName || data?.name || orgId,
        logoUrl: logo && /^https:\/\//.test(logo) ? logo : undefined,
        color: data?.branding?.primaryColor,
        url: communityUrl(orgId),
      });
      setStatus('found');
    } catch {
      // Can't verify right now; still let them try the address directly.
      setFound({ id: orgId, name: `${orgId}.${ROOT_DOMAIN}`, url: communityUrl(orgId) });
      setStatus('found');
    }
  };

  return (
    <>
      <section className="page-head">
        <div className="container">
          <h1>Find your community</h1>
          <p>
            Every organization on {BRAND} has its own sign-in page. Enter your community’s address
            to go there, or use the app to search for it.
          </p>
        </div>
      </section>

      <section className="page-body">
        <div className="container find">
          <div className="form-card">
            <h2>Go to your community</h2>
            <p className="muted small" style={{ marginBottom: 18 }}>
              It’s usually in your invite email, and looks like <strong>yourorg.{ROOT_DOMAIN}</strong>.
            </p>
            <form onSubmit={lookup} noValidate>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="si-address">Community address</label>
                <div className="find-go">
                  <div className="subdomain-input">
                    <input
                      id="si-address"
                      value={address}
                      onChange={(e) => { setAddress(e.target.value); if (status !== 'checking') setStatus('idle'); }}
                      placeholder="yourorg"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      inputMode="url"
                      aria-describedby={status === 'err' ? 'si-error' : undefined}
                      aria-invalid={status === 'err'}
                    />
                    <span className="subdomain-suffix" aria-hidden="true">.{ROOT_DOMAIN}</span>
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={status === 'checking'}>
                    {status === 'checking' ? 'Looking…' : 'Go'}
                  </button>
                </div>
              </div>
            </form>

            <div aria-live="polite">
              {status === 'err' && (
                <p id="si-error" className="subdomain-hint invalid" style={{ marginTop: 10 }}>
                  <ErrIcon style={{ fontSize: 17 }} aria-hidden="true" /> {error}
                </p>
              )}
              {status === 'found' && found && (
                <div className="found">
                  <span className="found-mark" style={{ background: found.color || CRIMSON }} aria-hidden="true">
                    {found.logoUrl ? <img src={found.logoUrl} alt="" /> : found.name.charAt(0).toUpperCase()}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <b>{found.name}</b>
                    <span>{found.url.replace(/^https:\/\//, '')}</span>
                  </div>
                  <a className="btn btn-primary" href={found.url}>Continue to sign in</a>
                </div>
              )}
            </div>

            <p className="muted small" style={{ marginTop: 26 }}>
              Don’t know your address? Download the app, create your login, and search for your
              organization by name. Your admins approve your request to join.
            </p>
          </div>

          <div className="side-stack">
            <div className="side-card">
              <h3>Get the app</h3>
              <p>Sign in once and switch between every community you belong to.</p>
              <StoreBadges />
            </div>
            <div className="side-card">
              <h3>Starting a new community?</h3>
              <p>Set up your organization with its own address, logo, and colors.</p>
              <Lnk to="/get-started" className="btn btn-outline">Create your community</Lnk>
            </div>
            <p className="muted small">
              Platform admin? <a className="text-link" href={ADMIN_CONSOLE_URL}>Sign in to the console</a>
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default SignIn;
